import { useState, useCallback } from 'react';

/**
 * Hook that provides an in-app confirmation dialog replacement for window.confirm.
 *
 * Usage:
 *   const { confirm, ConfirmDialog } = useConfirm();
 *
 *   const handleDelete = async () => {
 *     const ok = await confirm({ title: 'Delete item?', message: 'This cannot be undone.' });
 *     if (!ok) return;
 *     // proceed with delete
 *   };
 *
 *   return <>{ConfirmDialog}  ... rest of JSX</>;
 */
const useConfirm = () => {
  const [state, setState] = useState({ open: false, resolve: null, options: {} });

  const confirm = useCallback((options = {}) => {
    return new Promise((resolve) => {
      setState({ open: true, resolve, options });
    });
  }, []);

  const handleConfirm = useCallback(() => {
    state.resolve?.(true);
    setState({ open: false, resolve: null, options: {} });
  }, [state.resolve]);

  const handleCancel = useCallback(() => {
    state.resolve?.(false);
    setState({ open: false, resolve: null, options: {} });
  }, [state.resolve]);

  const dialog = state.open ? {
    open: true,
    onConfirm: handleConfirm,
    onCancel: handleCancel,
    ...state.options,
  } : { open: false };

  return { confirm, confirmDialogProps: dialog };
};

export default useConfirm;
